import { useRef, useEffect, useCallback } from 'react';

// Hook to capture and forward console logs to the audio dashboard
export function useConsoleLogCapture(addLog) {
  const originalConsoleRef = useRef({});

  const captureLog = useCallback((level, args) => {
    // Format the console arguments into a readable message
    const message = args.map(arg => {
      if (typeof arg === 'string') {
        return arg;
      } else if (typeof arg === 'object' && arg !== null) {
        try {
          return JSON.stringify(arg, null, 2);
        } catch (e) {
          return String(arg);
        }
      } else {
        return String(arg);
      }
    }).join(' ');

    // Extract category from common log patterns
    let category = 'system';
    if (message.includes('🎵') || message.includes('useAudioPlayer') || message.includes('Audio')) {
      category = 'audio';
    } else if (message.includes('performance') || message.includes('Performance') || message.includes('CPU') || message.includes('memory')) {
      category = 'performance';
    } else if (message.includes('visualizer') || message.includes('Visualizer')) {
      category = 'visualizer';
    }

    // Filter out noisy logs we don't need in the dashboard
    const shouldCapture = 
      message.includes('useAudioPlayer') ||
      message.includes('Audio') ||
      message.includes('buffer') ||
      message.includes('underrun') ||
      message.includes('dropout') ||
      message.includes('performance') ||
      message.includes('error') ||
      message.includes('warn') ||
      (level === 'error' || level === 'warn');

    if (shouldCapture && addLog) {
      addLog(level, category, message);
    }
  }, [addLog]);

  useEffect(() => {
    // Store original console methods
    originalConsoleRef.current = {
      log: console.log,
      warn: console.warn,
      error: console.error,
      info: console.info
    };

    // Override console methods to capture logs
    console.log = (...args) => {
      originalConsoleRef.current.log(...args);
      captureLog('info', args);
    };

    console.warn = (...args) => {
      originalConsoleRef.current.warn(...args);
      captureLog('warn', args);
    };

    console.error = (...args) => {
      originalConsoleRef.current.error(...args);
      captureLog('error', args);
    };

    console.info = (...args) => {
      originalConsoleRef.current.info(...args);
      captureLog('info', args);
    };

    return () => {
      // Restore original console methods
      console.log = originalConsoleRef.current.log;
      console.warn = originalConsoleRef.current.warn;
      console.error = originalConsoleRef.current.error;
      console.info = originalConsoleRef.current.info;
    };
  }, [captureLog]);

  return null; // This hook doesn't render anything
}
